import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import type { Role, StaffUser } from '../api/types'
import { Perm } from '../api/types'
import { formatMoment } from '../ui/format'
import { suggestPassword } from '@shop/shared'
import { Banner, Button, Empty, ErrorBanner, Field, Panel, Spinner, TextInput } from '@shop/ui'

/**
 * الموظّفون: القائمةُ وإنشاءُ حساب.
 *
 * <b>وكلمةُ المرور هنا مؤقّتةٌ بالضرورة</b>: الخادمُ يرفع
 * <c>must_change_password</c> على كلّ حسابٍ يُنشأ، فلا يعمل بها الموظّفُ إلا
 * حتى أوّل دخول. وهذا ليس تشدّدًا: كلمةٌ يعرفها من أنشأ الحسابَ تعني أنّ كلَّ
 * ما يفعله صاحبُها يمكن أن يكون فعلَ غيره — ويسقط معنى «من فعل هذا» كلُّه.
 */

/** أسماءُ الأدوار العربيّة تأتي من الخادم؛ وهذا شرحُ ما يفعله كلٌّ منها. */
const roleHints: Record<string, string> = {
  OWNER: 'كلُّ شيء. حسابٌ واحدٌ لا يُستعمل للعمل اليوميّ.',
  MANAGER: 'كلُّ شيءٍ عدا إدارة المستخدمين، وإعادةِ فتح فترة الافتتاح، وسجلِّ التدقيق.',
  REVIEWER: 'يراجع ويعتمد ويسعّر، ويرى الكلفة.',
  DATA_ENTRY: 'يُملي ويصحّح — ولا يعتمد، ولا يرى الكلفة، ولا يضع سعرًا.',
  VIEWER: 'قراءةٌ فقط، وبلا كلفة.',
}

function CreateStaff({ roles, onCreated }: { roles: Role[]; onCreated: () => void }) {
  const [username, setUsername] = useState('')
  const [fullNameAr, setFullNameAr] = useState('')
  const [password, setPassword] = useState('')
  const [roleId, setRoleId] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [created, setCreated] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: async () => {
      const user = await api<StaffUser>('/api/v1/users', {
        method: 'POST',
        body: { username: username.trim(), fullNameAr: fullNameAr.trim(), password },
      })

      // الدورُ في نداءٍ ثانٍ: إنشاءُ الحساب ومنحُ الدور صلاحيّتان مستقلّتان على
      // الخادم، فمن يُنشئ موظّفًا لا يصير بذلك من يمنحه المالكيّة.
      if (roleId) {
        await api<StaffUser>(`/api/v1/users/${user.id}/roles`, {
          method: 'PUT',
          body: { roleIds: [roleId] },
        })
      }

      return user
    },
    onSuccess: (user) => {
      setError(null)
      setCreated(user.username)

      // ⚠ الكلمةُ تُمحى من الحالة فورًا — لا تبقى في الذاكرة بعد أن أدّت غرضَها.
      setUsername('')
      setFullNameAr('')
      setPassword('')
      onCreated()
    },
    onError: setError,
  })

  const ready = username.trim().length > 0 && fullNameAr.trim().length > 0 && password.length >= 10

  return (
    <Panel title="موظّفٌ جديد">
      {created && (
        <div className="mb-3">
          <Banner tone="ok">
            أُنشئ «{created}». سلّمه الكلمةَ المؤقّتة <b>باليد</b> — والنظامُ
            يُلزمه بتغييرها عند أوّل دخول، فلا تعمل بعدها.
          </Banner>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="اسم الدخول" hint="لاتينيٌّ بلا مسافات — والحالةُ لا تفرّق.">
          <TextInput value={username} onChange={setUsername} dir="ltr" disabled={create.isPending} />
        </Field>

        <Field label="الاسم الكامل بالعربيّة" hint="يظهر في كلّ شاشةٍ وفي سجلّ التدقيق.">
          <TextInput value={fullNameAr} onChange={setFullNameAr} disabled={create.isPending} />
        </Field>

        <Field label="الدور">
          <select
            className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-brand"
            value={roleId}
            disabled={create.isPending}
            onChange={(e) => setRoleId(e.target.value)}
          >
            <option value="">بلا دور (لا يستطيع شيئًا)</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.nameAr}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="كلمةٌ مؤقّتة"
          hint="عشرةُ محارفَ فأكثر. يغيّرها الموظّفُ عند أوّل دخولٍ إلزامًا."
        >
          <div className="flex gap-2">
            <TextInput value={password} onChange={setPassword} dir="ltr" disabled={create.isPending} />
            <Button onClick={() => setPassword(suggestPassword())} disabled={create.isPending}>
              اقترح
            </Button>
          </div>
        </Field>
      </div>

      {roleId && roleHints[roles.find((r) => r.id === roleId)?.code ?? ''] && (
        <p className="mt-3 text-xs text-ink-soft">
          {roleHints[roles.find((r) => r.id === roleId)!.code]}
        </p>
      )}

      <div className="mt-4">
        <ErrorBanner error={error} />
      </div>

      <div className="mt-3">
        <Button tone="primary" disabled={create.isPending || !ready} onClick={() => create.mutate()}>
          {create.isPending ? 'يُنشأ…' : 'إنشاء الحساب'}
        </Button>
      </div>

      <p className="mt-3 text-xs text-ink-soft">
        الفروعُ والمستودعاتُ تُمنح من صفحة الموظّف بعد إنشائه — <b>وبلا مستودعٍ
        مخوَّلٍ لا يستطيع اعتمادَ كميّة</b>.
      </p>
    </Panel>
  )
}

export default function Staff() {
  const queries = useQueryClient()
  const { can } = useAuth()
  const [showDisabled, setShowDisabled] = useState(false)

  const users = useQuery({
    queryKey: ['staff', showDisabled],
    queryFn: () => api<StaffUser[]>(`/api/v1/users?includeDisabled=${showDisabled}`),
  })

  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => api<Role[]>('/api/v1/roles'),
    enabled: can(Perm.roleView),
  })

  const names = new Map((roles.data ?? []).map((r) => [r.code, r.nameAr]))

  return (
    <>
      {can(Perm.userCreate) && (
        <CreateStaff
          roles={roles.data ?? []}
          onCreated={() => void queries.invalidateQueries({ queryKey: ['staff'] })}
        />
      )}

      <Panel
        title="الموظّفون"
        actions={
          <Button onClick={() => setShowDisabled((current) => !current)}>
            {showDisabled ? 'المفعَّلون وحدَهم' : 'أظهر المعطَّلين أيضًا'}
          </Button>
        }
      >
        {users.isPending && <Spinner />}
        {users.isError && <ErrorBanner error={users.error} />}
        {users.data?.length === 0 && <Empty>لا موظّفين بعد.</Empty>}

        <ul className="space-y-2">
          {(users.data ?? []).map((user) => (
            <li key={user.id}>
              <Link
                to={`/staff/${user.id}`}
                className={`block rounded-md border px-3 py-2 hover:bg-surface ${
                  user.isActive ? 'border-line' : 'border-warn/50 bg-warn/5'
                }`}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-medium">{user.fullNameAr}</span>
                  <span className="text-xs text-ink-soft" dir="ltr">
                    {user.username}
                  </span>
                </div>

                <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                  <span>{user.roleCodes.map((c) => names.get(c) ?? c).join(' · ') || 'بلا دور'}</span>
                  <span className="tabular">آخرُ دخول: {formatMoment(user.lastLoginAt)}</span>
                  {!user.isActive && <span className="text-warn">معطَّل</span>}
                  {user.mustChangePassword && <span>لم يغيّر كلمتَه بعد</span>}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </Panel>
    </>
  )
}
